import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Check, QrCode, Clock, AlertCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import ImageUploader from "@/components/ImageUploader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * PixPaymentDialog
 * Props:
 *   open, onOpenChange, purpose, amount, refId?, onConfirmed?
 * When "Já paguei" is clicked → posts /mark-paid → shows waiting UI.
 * Admin will confirm asynchronously; user can close and check later in "Meus PIX".
 */
export default function PixPaymentDialog({ open, onOpenChange, purpose, amount, refId, note, onCreated, onMarkedPaid }) {
  const [intent, setIntent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [marking, setMarking] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  const [receiptUrl, setReceiptUrl] = useState("");

  useEffect(() => {
    if (!open) { setIntent(null); setError(""); return; }
    if (intent) return;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.post(`${API}/pix/create`, {
          purpose, amount, ref_id: refId || null, note: note || "",
        });
        setIntent(r.data);
        onCreated?.(r.data);
      } catch (err) {
        console.error("[PixDialog] create failed", err);
        setError(err.response?.data?.detail || err.message || "Erro ao criar cobrança PIX");
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const copyKey = async () => {
    if (!intent?.pix_key) return;
    try {
      await navigator.clipboard.writeText(intent.pix_key);
      setCopied(true);
      toast.success("Chave PIX copiada!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("[PixDialog] copy failed", err);
      toast.info(`Chave: ${intent.pix_key}`);
    }
  };

  const markPaid = async () => {
    if (!intent) return;
    if (!receiptUrl) { toast.error("Anexe o comprovante do Pix antes de continuar"); return; }
    setMarking(true);
    try {
      await axios.post(`${API}/pix/${intent.payment_id}/mark-paid`, { receipt_url: receiptUrl });
      setIntent({ ...intent, status: "marked_paid" });
      onMarkedPaid?.(intent);
      toast.success("Marcamos como pago! Aguarde o admin confirmar (geralmente em minutos).");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao marcar como pago");
    }
    setMarking(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="pix-payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-primary" />
            Pagar com PIX
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="text-sm text-muted-foreground py-8 text-center">Gerando cobrança PIX...</div>
        )}

        {error && (
          <div className="p-4 rounded-lg border border-red-500/40 bg-red-500/10 flex items-start gap-2" data-testid="pix-error">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-700 dark:text-red-300">{error}</div>
          </div>
        )}

        {intent && intent.status === "marked_paid" && (
          <div className="p-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 space-y-2" data-testid="pix-waiting-confirm">
            <div className="flex items-center gap-2 font-bold text-emerald-700 dark:text-emerald-300">
              <Clock className="w-5 h-5" /> Aguardando confirmação do admin
            </div>
            <p className="text-sm text-muted-foreground">
              Recebemos seu aviso de pagamento. Assim que o admin confirmar o recebimento em conta, seu crédito/plano será aplicado automaticamente. Você receberá uma notificação.
            </p>
            <div className="text-xs text-muted-foreground">
              ID: <span className="font-mono">{intent.payment_id}</span>
            </div>
          </div>
        )}

        {intent && intent.status === "pending" && (
          <div className="space-y-4" data-testid="pix-intent-pending">
            <div className="p-4 rounded-lg bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/40 space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Valor a pagar</div>
                <div className="text-3xl font-bold" data-testid="pix-amount">R$ {Number(intent.amount).toFixed(2)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Chave PIX ({intent.pix_key_type.toUpperCase()})</div>
                <div className="flex items-center gap-2 mt-1">
                  <code className="font-mono text-sm break-all flex-1" data-testid="pix-key">{intent.pix_key}</code>
                  <Button size="sm" variant="outline" onClick={copyKey} data-testid="pix-copy-key" className="rounded-full flex-shrink-0">
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Beneficiário</div>
                <div className="text-sm">{intent.beneficiary_name}</div>
              </div>
            </div>

            <ol className="text-xs text-muted-foreground space-y-1 pl-4 list-decimal">
              <li>Abra o app do seu banco e faça um PIX para a chave acima</li>
              <li>Confira o valor exato: <strong>R$ {Number(intent.amount).toFixed(2)}</strong></li>
              <li>Após pagar, anexe o <strong>comprovante</strong> e clique em <strong>&quot;Já paguei&quot;</strong></li>
              <li>O admin vai confirmar o recebimento em minutos e seu crédito/plano será aplicado</li>
            </ol>

            <div className="rounded-xl border border-dashed p-3 flex items-center gap-3" data-testid="pix-receipt-box">
              {receiptUrl ? (
                <img src={receiptUrl} alt="comprovante" className="w-14 h-14 rounded object-cover border" data-testid="pix-receipt-preview" />
              ) : (
                <div className="w-14 h-14 rounded bg-muted flex items-center justify-center text-muted-foreground text-[10px] text-center leading-tight">sem<br/>comprovante</div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold">Comprovante do Pix {receiptUrl ? "anexado ✓" : "(obrigatório)"}</div>
                <div className="text-[11px] text-muted-foreground">Foto ou print do comprovante do seu banco.</div>
              </div>
              <ImageUploader testid="pix-receipt-upload" label={receiptUrl ? "Trocar" : "Anexar"} size="sm" onUploaded={(url) => setReceiptUrl(url)} />
            </div>

            <Button
              data-testid="pix-mark-paid"
              onClick={markPaid}
              disabled={marking}
              className="w-full rounded-full h-11 bg-emerald-500 hover:bg-emerald-600 text-white"
            >
              {marking ? "Enviando..." : "Já paguei"}
            </Button>
            <button
              onClick={() => onOpenChange(false)}
              data-testid="pix-cancel"
              className="w-full text-xs text-muted-foreground hover:text-foreground py-1"
            >
              Fechar sem pagar
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
